const API_BASE = import.meta.env.VITE_API_URL || "";

let onTokenRefreshed: ((token: string) => void) | null = null;
let onAuthFailed: (() => void) | null = null;
let refreshPromise: Promise<string | null> | null = null;

export function setAuthCallbacks(
  onRefreshed: (token: string) => void,
  onFailed: () => void,
) {
  onTokenRefreshed = onRefreshed;
  onAuthFailed = onFailed;
}

function getToken(): string | null {
  return localStorage.getItem("auth_token");
}

function getRefreshToken(): string | null {
  return localStorage.getItem("auth_refresh_token");
}

async function attemptRefresh(): Promise<string | null> {
  const rt = getRefreshToken();
  if (!rt) return null;

  // Deduplicate concurrent refresh attempts
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ refreshToken: rt }),
      });

      if (!res.ok) return null;

      const data = await res.json();
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_refresh_token", data.refreshToken);
      onTokenRefreshed?.(data.token);
      return data.token as string;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    const token = getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  private async handleResponse(res: Response, retry: () => Promise<Response>): Promise<Response> {
    if (res.status === 401) {
      const newToken = await attemptRefresh();
      if (newToken) {
        return retry();
      }
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_refresh_token");
      onAuthFailed?.();
    }
    return res;
  }

  async get(path: string, params?: Record<string, string>): Promise<Response> {
    const url = new URL(`${this.baseUrl}${path}`, window.location.origin);
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        url.searchParams.set(key, value);
      }
    }

    const doFetch = () =>
      fetch(url.toString(), { headers: this.getHeaders(), credentials: "include" });

    const res = await doFetch();
    return this.handleResponse(res, doFetch);
  }

  async post(path: string, body?: unknown): Promise<Response> {
    const doFetch = () =>
      fetch(`${this.baseUrl}${path}`, {
        method: "POST",
        headers: this.getHeaders(),
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });

    const res = await doFetch();
    return this.handleResponse(res, doFetch);
  }

  async patch(path: string, body?: unknown): Promise<Response> {
    const doFetch = () =>
      fetch(`${this.baseUrl}${path}`, {
        method: "PATCH",
        headers: this.getHeaders(),
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });

    const res = await doFetch();
    return this.handleResponse(res, doFetch);
  }

  async put(path: string, body?: unknown): Promise<Response> {
    const doFetch = () =>
      fetch(`${this.baseUrl}${path}`, {
        method: "PUT",
        headers: this.getHeaders(),
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });

    const res = await doFetch();
    return this.handleResponse(res, doFetch);
  }

  async delete(path: string, body?: unknown): Promise<Response> {
    const doFetch = () =>
      fetch(`${this.baseUrl}${path}`, {
        method: "DELETE",
        headers: this.getHeaders(),
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });

    const res = await doFetch();
    return this.handleResponse(res, doFetch);
  }
}

export const api = new ApiClient(API_BASE);
